import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { PLANS, DOWNLOAD_URL } from "../../lib/data";
import { useLanguage } from "../../lib/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

const PlanCard = ({ plan, index, t }) => {
  const [loading, setLoading] = useState(false);
  const p = `pricing.plans.${plan.key}`;
  const features = t(`${p}.features`);

  const handleClick = async () => {
    if (!plan.lookup_key) {
      window.open(DOWNLOAD_URL, "_blank", "noopener");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/payments/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lookup_key: plan.lookup_key, origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error("Could not start checkout. Please try again.");
        setLoading(false);
      }
    } catch (e) {
      toast.error("Network error. Please try again.");
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, ease: EASE, delay: index * 0.1 }}
      className={`relative rounded-3xl p-8 md:p-10 flex flex-col ${
        plan.highlight ? "bg-gradient-to-b from-blue-500/[0.12] to-[#0A101F] border border-blue-400/30 glow-blue lg:-translate-y-4" : "bg-[#0A101F] border border-white/[0.06]"
      }`}
      data-testid={`pricing-card-${plan.key}`}
    >
      {plan.highlight && (
        <span className="absolute top-6 right-6 text-[10px] uppercase tracking-[0.2em] font-bold text-blue-300 bg-blue-500/15 border border-blue-400/30 px-3 py-1 rounded-full">
          {t("pricing.mostPopular")}
        </span>
      )}
      <h3 className="font-head text-2xl tracking-tight mb-1">{plan.name}</h3>
      <p className="text-sm text-slate-500 mb-6">{t(`${p}.tagline`)}</p>
      <div className="flex items-baseline gap-2 mb-8">
        <span className="font-head font-semibold text-5xl tracking-tighter">{plan.price}</span>
        <span className="text-slate-500 text-sm">/ {t(`pricing.${plan.periodKey}`)}</span>
      </div>

      <ul className="space-y-3.5 mb-10 flex-1">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-3 text-slate-300">
            <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-blue-500/15 flex items-center justify-center">
              <Check size={12} className="text-blue-400" strokeWidth={3} />
            </span>
            <span className="text-[15px]">{f}</span>
          </li>
        ))}
      </ul>

      <button
        onClick={handleClick}
        disabled={loading}
        className={`group inline-flex items-center justify-center gap-2 w-full py-4 rounded-full font-semibold transition-colors duration-300 disabled:opacity-70 ${
          plan.highlight ? "bg-blue-500 text-white hover:bg-blue-600" : "bg-white/[0.04] border border-white/10 text-white hover:bg-white/[0.08]"
        }`}
        data-testid={`pricing-cta-${plan.key}`}
      >
        {loading ? (
          <>
            <Loader2 size={18} className="animate-spin" /> …
          </>
        ) : (
          <>
            {t(`${p}.cta`)}
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
          </>
        )}
      </button>
    </motion.div>
  );
};

export const Pricing = () => {
  const { t } = useLanguage();
  return (
    <section id="pricing" className="relative py-24 md:py-36" data-testid="pricing-section">
      <div className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-600/10 rounded-full blur-[160px]" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-xs uppercase tracking-[0.2em] text-blue-400 font-bold">{t("pricing.label")}</span>
          <h2 className="font-head font-semibold text-4xl md:text-5xl lg:text-6xl tracking-tight leading-tight mt-4">{t("pricing.title")}</h2>
          <p className="text-slate-400 mt-5 text-lg">{t("pricing.subtitle")}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          {PLANS.map((plan, i) => (
            <PlanCard key={plan.key} plan={plan} index={i} t={t} />
          ))}
        </div>
        <p className="text-center text-xs text-slate-600 mt-10">{t("pricing.secure")}</p>
      </div>
    </section>
  );
};
